/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { FileText, Landmark, Scale } from 'lucide-react';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { IMPRINT_SECTIONS } from '@/config/footerContent';

function ImprintTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Imprint')}</h2>
                <p>{t('Official company information and legal notices for EA Systems Dresden GmbH.')}</p>
            </section>

            <section className="grid gap-4 xl:grid-cols-3">
                <article className="dashboard-panel items-stretch justify-start p-0 xl:col-span-2">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex min-w-0 items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                    <Landmark className="h-6 w-6" aria-hidden="true" />
                                </div>
                                <div className="min-w-0 space-y-1">
                                    <h3>{IMPRINT_SECTIONS.provider.title}</h3>
                                    <p>{t('Source-aligned company information')}</p>
                                </div>
                            </div>
                            <div className="contact-company-logo" aria-hidden="true">
                                <img
                                    className="contact-company-logo__image contact-company-logo__image--light"
                                    src="/logo/easd_logo_light.png"
                                    alt=""
                                />
                                <img
                                    className="contact-company-logo__image contact-company-logo__image--dark"
                                    src="/logo/easd_logo_dark.png"
                                    alt=""
                                />
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                <h4>{IMPRINT_SECTIONS.provider.title}</h4>
                                <div className="space-y-1 text-sm">
                                    {IMPRINT_SECTIONS.provider.content.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                <h4>{IMPRINT_SECTIONS.managingDirector.title}</h4>
                                <div className="space-y-1 text-sm">
                                    {IMPRINT_SECTIONS.managingDirector.content.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                <h4>{IMPRINT_SECTIONS.contact.title}</h4>
                                <div className="space-y-1 text-sm">
                                    {IMPRINT_SECTIONS.contact.content.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                <h4>{IMPRINT_SECTIONS.taxInfo.title}</h4>
                                <div className="space-y-1 text-sm">
                                    {IMPRINT_SECTIONS.taxInfo.content.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </article>

                <article className="dashboard-panel items-stretch justify-start p-0">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-secondary">
                                <Scale className="h-5 w-5" aria-hidden="true" />
                            </div>
                            <div className="space-y-1">
                                <h3>{t('Legal overview')}</h3>
                                <p>{t('Key registration and tax details')}</p>
                            </div>
                        </div>
                        <div className="space-y-3 text-sm">
                            {IMPRINT_SECTIONS.taxInfo.content.map((line) => (
                                <div
                                    key={line}
                                    className="rounded-box border border-base-300 bg-base-100/70 px-4 py-3"
                                >
                                    {line}
                                </div>
                            ))}
                        </div>
                    </div>
                </article>
            </section>

            <section className="dashboard-panel items-stretch justify-start p-0">
                <div className="flex flex-col gap-5 p-6 w-full h-full">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-accent">
                            <FileText className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <div className="space-y-1">
                            <h3>{t('Legal notices')}</h3>
                            <p>{t('Detailed legal information from the company imprint')}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {IMPRINT_SECTIONS.legalNotes.map((section) => (
                            <div
                                key={section.title}
                                className="collapse collapse-arrow rounded-box border border-base-300 bg-base-100/70"
                            >
                                <input type="checkbox" />
                                <div className="collapse-title text-base font-semibold">
                                    {section.title}
                                </div>
                                <div className="collapse-content space-y-3 text-sm leading-6 text-base-content/80">
                                    {section.content.map((paragraph) => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default ImprintTab;
